import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from "react";
import { string, bool } from "prop-types";
import SSRIcon from "@ingka/ssr-icon";
import imageStrikethrough from "@ingka/ssr-icon/paths/image-strikethrough";
import imageFallback from "@ingka/ssr-icon/paths/image";
import cx from "classnames";
const propTypes = {
  /** Path to the smallest default img tag */
  src: string,
  /** Can be used to add classes to base HTML element. */
  className: string,
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Specifies an alternate text for an img tag */
  alt: string,
  /** Value for `srcset` attribute for img tag */
  srcSet: string,
  /** Value for `sizes` attribute for img tag */
  sizes: string,
  /** Value for `loading` attribute for img tag */
  loading: string,
  /** Force usage of fallback for image */
  fallback: bool
};
const Image = (props) => {
  const {
    src,
    alt,
    className,
    prefix = "",
    srcSet,
    sizes,
    loading = "lazy",
    fallback,
    ...otherProps
  } = props;
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imageRef = useRef(null);
  const classes = cx(
    `${prefix}image`,
    (error || fallback) && `${prefix}image--fallback`,
    className
  );
  const onError = (errorEvent) => {
    if (!imageRef.current)
      return;
    setError(true);
    if (otherProps.onError)
      otherProps.onError(errorEvent);
  };
  const onLoad = (loadEvent) => {
    if (!imageRef.current)
      return;
    setLoaded(true);
    if (otherProps.onLoad)
      otherProps.onLoad(loadEvent);
  };
  useEffect(() => {
    if (src) {
      setError(false);
    }
  }, [src]);
  useEffect(() => {
    if (imageRef.current && !(loaded || error)) {
      if (imageRef.current.complete) {
        if (imageRef.current.naturalHeight) {
          const artificialLoadEvent = new Event("load");
          imageRef.current.dispatchEvent(artificialLoadEvent);
        } else {
          const artificialErrorEvent = new Event("error");
          imageRef.current.dispatchEvent(artificialErrorEvent);
        }
      }
    }
  }, []);
  let paths = imageFallback;
  if (error) {
    paths = imageStrikethrough;
  }
  const image = /* @__PURE__ */ jsx(
    "img",
    {
      ref: imageRef,
      className: !error && !fallback ? classes : void 0,
      onError,
      onLoad,
      alt,
      src,
      srcSet,
      sizes,
      loading,
      ...otherProps
    }
  );
  return error || fallback ? /* @__PURE__ */ jsxs("span", { className: classes, children: [
    /* @__PURE__ */ jsx(SSRIcon, { paths, className: `${prefix}image__icon`, prefix }),
    image
  ] }) : image;
};
Image.propTypes = propTypes;
var Image_default = Image;
export {
  Image_default as default
};
